import './searchList.scss';

import React, { useEffect, useState } from 'react';

import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { SearchListProduct } from './singles/SearchListProduct';

enum CATEGORIES_KEYS {
    Heaters = 'heaters',
    Venting = 'venting',
    Accessories = 'accessories',
    Parts = 'parts',
}

type SearchListProps = {
    searchTerms: string;
    allProducts: any[];
    t: any;
    isSearchListOpen: boolean;
    setSearchListOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const RESULTS_FETCH_LIMIT = 40;
const MINIMUM_SCORE = 0.5;

export default function SearchList(props: SearchListProps) {
    const { searchTerms, allProducts, t, isSearchListOpen, setSearchListOpen } =
        props;

    const [productsGroupedByCategories, setProductsGroupedByCategories] =
        useState<Map<string, any>>(new Map());
    const [isAfterFetch, setAfterFetch] = useState<boolean>(false);
    const { currentLocale } = usePageLocalization();

    useEffect(() => {
        if (searchTerms.length >= 2) {
            fetch(
                `/search?q=${searchTerms}&limit=${RESULTS_FETCH_LIMIT}&filter=NOT (category.en-CA.keyword: "/OCC/Parts/") AND NOT (category.en-CA.keyword: "/OCC/Options/")`
            )
                .then((response) => response.json())
                .then((data) => {
                    const filteredSearchResults = filterSearchResults(
                        data?.body?.hits?.hits
                    );

                    const searchResultsGraphQLMapping = getGraphQLInfo(
                        getObjectSourceData(filteredSearchResults)
                    );

                    setProductsGroupedByCategories(
                        groupByCategories(searchResultsGraphQLMapping)
                    );

                    setSearchListOpen(true);
                    setAfterFetch(true);
                });
        } else {
            setSearchListOpen(false);
        }
    }, [searchTerms, setSearchListOpen, setProductsGroupedByCategories]);

    const filterSearchResults = (searchResults: any[]): any[] => {
        return searchResults.filter((searchResult: any) => {
            const category =
                searchResult?._source?.category?.['en-CA']?.toLowerCase();

            const categoriesToShow = [
                CATEGORIES_KEYS.Accessories,
                CATEGORIES_KEYS.Heaters,
                CATEGORIES_KEYS.Parts,
            ];

            if (currentLocale !== 'en_US') {
                categoriesToShow.push(CATEGORIES_KEYS.Venting);
            }

            return (
                categoriesToShow.some((str) => category?.includes(str)) &&
                searchResult._score > MINIMUM_SCORE
            );
        });
    };

    const groupByCategories = (searchResults: any[]): Map<string, any> => {
        const map = new Map();

        searchResults.forEach((item) => {
            const categoryKey = getCategoryKey(item?.slug?.['en_CA']);

            const categoryAndProductsRecord = map.get(categoryKey);

            if (!categoryAndProductsRecord) {
                map.set(categoryKey, [item]);
            } else {
                categoryAndProductsRecord.push(item);
            }
        });

        return map;
    };

    const getObjectSourceData = (elasticObject: any[]) => {
        let newObject: any[] = [];
        elasticObject.forEach((object) => {
            newObject.push(object._source);
        });
        return newObject;
    };

    const getGraphQLInfo = (searchResultsItems: any[]): any[] => {
        const itemCodes = searchResultsItems?.map((item) => item.code);

        return allProducts?.filter((p: any) => itemCodes.includes(p?.code));
    };

    const getCategoryKey = (slug: string | undefined): string => {
        if (!slug) {
            return 'NR';
        }
        if (slug.includes('/accessories/') || slug.includes('/options/')) {
            return CATEGORIES_KEYS.Accessories;
        } else if (
            slug.includes('/heaters/') ||
            slug.includes('/discontinued/')
        ) {
            return CATEGORIES_KEYS.Heaters;
        } else if (slug.includes('/parts/')) {
            return CATEGORIES_KEYS.Parts;
        } else if (slug.includes('/venting/')) {
            return CATEGORIES_KEYS.Venting;
        } else {
            return 'NR';
        }
    };

    if (isSearchListOpen) {
        return (
            <div className='search-list-box'>
                {productsGroupedByCategories?.size > 0
                    ? Array.from(productsGroupedByCategories).map(
                          (result: any) => {
                              const category = result?.[0];
                              const elements = result?.[1];

                              if (
                                  category === CATEGORIES_KEYS.Accessories ||
                                  category === CATEGORIES_KEYS.Heaters ||
                                  category === CATEGORIES_KEYS.Venting ||
                                  category === CATEGORIES_KEYS.Parts
                              ) {
                                  return (
                                      <div
                                          className={`search-${category}-section`}
                                          key={category}
                                      >
                                          {elements.map((product: any) => {
                                              return (
                                                  <SearchListProduct
                                                      key={`product-single-${product?.code}`}
                                                      product={product}
                                                  />
                                              );
                                          })}
                                      </div>
                                  );
                              }
                          }
                      )
                    : isAfterFetch && (
                          <p className='no-result'>
                              {t('pages:search.no-results')}
                          </p>
                      )}
            </div>
        );
    }
}
